/**
 * Enrichment API — public OTP + email verification, then Bearer JWT (1h).
 * Base path: /enrichment (relative to API_BASE_URL).
 */

import { getEnrichmentAccessToken } from "@/lib/enrichment-session";

const API_BASE_URL =
	process.env.NEXT_PUBLIC_API_BASE_URL || "https://api.taxlienvest.com";

function toQuery(params: Record<string, string | number | undefined>): string {
	const search = new URLSearchParams();
	for (const [k, v] of Object.entries(params)) {
		if (v !== undefined && v !== "") search.set(k, String(v));
	}
	const q = search.toString();
	return q ? `?${q}` : "";
}

async function authFetch(url: string, init?: RequestInit): Promise<Response> {
	const headers = new Headers(init?.headers);
	const token =
		typeof window !== "undefined" ? getEnrichmentAccessToken() : null;
	if (token) headers.set("Authorization", `Bearer ${token}`);

	const body = init?.body;
	if (
		body &&
		!(body instanceof FormData) &&
		typeof body === "string" &&
		!headers.has("Content-Type")
	) {
		headers.set("Content-Type", "application/json");
	}

	return fetch(url, { ...init, headers });
}

function messageFromApiBody(err: unknown, fallback: string): string {
	if (typeof err !== "object" || err === null || !("message" in err))
		return fallback;
	const m = (err as { message?: unknown }).message;
	if (typeof m === "string" && m.trim()) return m;
	if (Array.isArray(m)) return m.map(String).join(" ");
	return fallback;
}

// --- Config (feature flag) ---

export interface ConfigResponse {
	enrichment: boolean;
}

export async function getConfig(): Promise<ConfigResponse> {
	const res = await fetch(`${API_BASE_URL}/config`);
	if (!res.ok) {
		const err = await res.json().catch(() => ({}));
		throw new Error(
			(err as { message?: string }).message || "Failed to get config"
		);
	}
	return res.json();
}

// --- Email OTP ---

export interface EnrichmentPropertyQuota {
	used: number;
	limit: number;
	remaining: number;
}

export interface VerifyOtpResponse {
	accessToken: string;
	expiresIn: number;
	email: string;
	enrichmentPublicEntityId: string;
	hasConfiguration: boolean;
	interests?: Array<{ state: string; county: string }>;
	propertyQuota: EnrichmentPropertyQuota;
}

export async function requestEnrichmentOtp(email: string): Promise<{
	success: true;
	message: string;
}> {
	const res = await fetch(`${API_BASE_URL}/enrichment/request-otp`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ email }),
	});
	if (!res.ok) {
		const err = await res.json().catch(() => ({}));
		throw new Error(messageFromApiBody(err, "Could not send code"));
	}
	return res.json();
}

export async function resendEnrichmentOtp(email: string): Promise<{
	success: true;
	message: string;
}> {
	const res = await fetch(`${API_BASE_URL}/enrichment/resend-otp`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ email }),
	});
	if (!res.ok) {
		const err = await res.json().catch(() => ({}));
		throw new Error(messageFromApiBody(err, "Could not resend code"));
	}
	return res.json();
}

export async function verifyEnrichmentOtp(
	email: string,
	otp: string
): Promise<VerifyOtpResponse> {
	const res = await fetch(`${API_BASE_URL}/enrichment/verify-otp`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ email, otp }),
	});
	if (!res.ok) {
		const err = await res.json().catch(() => ({}));
		throw new Error(messageFromApiBody(err, "Verification failed"));
	}
	return res.json();
}

// --- Investor details (Bearer required) ---

export interface InvestorDetailsResponse {
	enrichmentPublicEntityId: string;
	email: string;
	hasConfiguration: boolean;
	interests?: Array<{ state: string; county: string }>;
	propertyQuota: EnrichmentPropertyQuota;
}

export async function getInvestorDetails(): Promise<InvestorDetailsResponse> {
	const res = await authFetch(`${API_BASE_URL}/enrichment/investor-details`);
	if (!res.ok) {
		const err = await res.json().catch(() => ({}));
		throw new Error(
			messageFromApiBody(err, "Failed to get investor details")
		);
	}
	return res.json();
}

// --- Save interests (county selection) ---

export async function saveInterests(
	interests: Array<{ state: string; county: string }>
): Promise<void> {
	const res = await authFetch(`${API_BASE_URL}/enrichment/interests`, {
		method: "POST",
		body: JSON.stringify({ interests }),
	});
	if (!res.ok) {
		const err = await res.json().catch(() => ({}));
		throw new Error(
			(err as { message?: string }).message || "Failed to save interests"
		);
	}
}

// --- Supported counties ---

export interface SupportedCounty {
	state: string;
	county: string;
}

export async function getSupportedCounties(): Promise<SupportedCounty[]> {
	const res = await fetch(`${API_BASE_URL}/enrichment/supported-counties`);
	if (!res.ok) throw new Error("Failed to load supported counties");
	return res.json();
}

// --- Validate county (single state/county) ---

export interface ValidateCountyResponse {
	supported: boolean;
}

export async function validateCounty(
	state: string,
	county: string
): Promise<ValidateCountyResponse> {
	const res = await fetch(`${API_BASE_URL}/enrichment/validate-county`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ state, county }),
	});
	if (!res.ok) {
		const err = await res.json().catch(() => ({}));
		throw new Error(
			(err as { message?: string }).message || "Validation failed"
		);
	}
	return res.json();
}

// --- Validate interests (batch; optional backend endpoint) ---

export interface ValidateInterestsResultItem {
	state: string;
	county: string;
	supported: boolean;
}

export interface ValidateInterestsResponse {
	valid: boolean;
	results: ValidateInterestsResultItem[];
}

export async function validateInterests(
	interests: Array<{ state: string; county: string }>
): Promise<ValidateInterestsResponse> {
	const res = await authFetch(
		`${API_BASE_URL}/enrichment/validate-interests`,
		{
			method: "POST",
			body: JSON.stringify({ interests }),
		}
	);
	if (!res.ok) {
		const err = await res.json().catch(() => ({}));
		throw new Error(
			(err as { message?: string }).message || "Validation failed"
		);
	}
	return res.json();
}

// --- Configs ---

export interface EnrichmentConfig {
	id: string;
	state: string;
	county: string;
	name: string;
	selected_columns: string[];
	createdAt?: string;
}

export async function getConfigs(): Promise<EnrichmentConfig[]> {
	const res = await authFetch(`${API_BASE_URL}/enrichment/configs`);
	if (!res.ok) throw new Error("Failed to load configs");
	return res.json();
}

export async function saveConfig(payload: {
	state: string;
	county: string;
	name: string;
	selected_columns: string[];
}): Promise<EnrichmentConfig> {
	const res = await authFetch(`${API_BASE_URL}/enrichment/configs`, {
		method: "POST",
		body: JSON.stringify(payload),
	});
	if (!res.ok) {
		const err = await res.json().catch(() => ({}));
		throw new Error(
			(err as { message?: string }).message || "Failed to save config"
		);
	}
	return res.json();
}

export async function deleteConfig(configId: string): Promise<{
	success: boolean;
}> {
	const res = await authFetch(
		`${API_BASE_URL}/enrichment/configs/${configId}`,
		{ method: "DELETE" }
	);
	if (!res.ok) throw new Error("Failed to delete config");
	return res.json();
}

// --- File upload ---

export interface UploadFileResponse {
	file_id: string;
	row_count: number;
	column_mapping?: Record<string, string>;
	sample: Record<string, unknown>[];
}

export async function uploadFile(file: File): Promise<UploadFileResponse> {
	const form = new FormData();
	form.append("file", file);
	const res = await authFetch(`${API_BASE_URL}/enrichment/files/upload`, {
		method: "POST",
		body: form,
	});
	if (!res.ok) {
		let message = "File upload failed";
		try {
			const err = await res.json();
			message =
				typeof err === "object" &&
				err !== null &&
				"message" in err &&
				typeof (err as { message: unknown }).message === "string"
					? (err as { message: string }).message
					: res.status === 400
						? "Invalid request. Check file format and size, or try signing in again."
						: message;
		} catch {
			message =
				res.status === 400
					? "Invalid request. Check file format and size, or try signing in again."
					: res.statusText || message;
		}
		throw new Error(message);
	}
	return res.json();
}

// --- File sample / rows ---

export interface FileSampleResponse {
	sample: Record<string, unknown>[];
	row_count: number;
}

export async function getFileSample(
	fileId: string
): Promise<FileSampleResponse> {
	const res = await authFetch(
		`${API_BASE_URL}/enrichment/files/${fileId}/sample`
	);
	if (!res.ok) throw new Error("Failed to load file sample");
	return res.json();
}

export interface FileRowsResponse<T = Record<string, unknown>> {
	data: T[];
	total: number;
	page: number;
	pageSize: number;
	totalPages: number;
}

export async function getFileRows(
	fileId: string,
	page: number = 1,
	pageSize: number = 20
): Promise<FileRowsResponse> {
	const q = toQuery({
		page: String(page),
		pageSize: String(pageSize),
	});
	const res = await authFetch(
		`${API_BASE_URL}/enrichment/files/${fileId}/rows${q}`
	);
	if (!res.ok) throw new Error("Failed to load file rows");
	return res.json();
}

// --- Available columns ---

export interface AvailableColumn {
	key: string;
	label: string;
	category: string;
	creditCost?: number;
}

export interface ColumnCategory {
	key: string;
	label: string;
	columns: AvailableColumn[];
}

export interface AvailableColumnsResponse {
	categories: ColumnCategory[];
}

export async function getAvailableColumns(
	state: string,
	county?: string
): Promise<AvailableColumnsResponse> {
	const params: Record<string, string> = { state };
	if (county) params.county = county;
	const q = toQuery(params);
	const res = await fetch(`${API_BASE_URL}/enrichment/available-columns${q}`);
	if (!res.ok) throw new Error("Failed to load available columns");
	return res.json();
}

// --- Submit enrichment ---

export interface SubmitEnrichmentResponse {
	enrichment_stat_id: string;
	status: string;
	rows_total: number;
	rows_completed?: number;
}

export async function submitEnrichment(payload: {
	file_id: string;
	columns_to_enrich: string[];
	config_id?: string;
	credits_utilised?: number;
}): Promise<SubmitEnrichmentResponse> {
	const res = await authFetch(`${API_BASE_URL}/enrichment/submit`, {
		method: "POST",
		body: JSON.stringify(payload),
	});
	if (!res.ok) {
		const err = await res.json().catch(() => ({}));
		throw new Error(messageFromApiBody(err, "Submit failed"));
	}
	return res.json();
}

// --- Enrichment stats ---

export interface EnrichmentStats {
	all: number;
	pending: number;
	in_progress: number;
	completed: number;
	partially_completed: number;
	failed: number;
}

export async function getEnrichmentStats(): Promise<EnrichmentStats> {
	const res = await authFetch(`${API_BASE_URL}/enrichment/stats`);
	if (!res.ok) throw new Error("Failed to get enrichment stats");
	return res.json();
}

// --- List enrichments ---

export type EnrichmentStatus =
	| "pending"
	| "in_progress"
	| "completed"
	| "partially_completed"
	| "failed";

export interface EnrichmentListItem {
	id: string;
	status: EnrichmentStatus;
	rows_total: number;
	rows_completed: number;
	rows_pending: number;
	rows_failed: number;
	/** Uploaded spreadsheet name (list + detail API). */
	original_file_name?: string;
	file?: { original_file_name?: string };
	[key: string]: unknown;
}

export interface ListEnrichmentsResponse {
	data: EnrichmentListItem[];
	total: number;
	page: number;
	pageSize: number;
	totalPages: number;
}

export async function listEnrichments(
	page: number = 1,
	pageSize: number = 20,
	status?: EnrichmentStatus
): Promise<ListEnrichmentsResponse> {
	const params: Record<string, string | number> = { page, pageSize };
	if (status) params.status = status;
	const q = toQuery(params);
	const res = await authFetch(`${API_BASE_URL}/enrichment${q}`);
	if (!res.ok) throw new Error("Failed to list enrichments");
	return res.json();
}

// --- Get single enrichment ---

export interface EnrichmentDetail extends EnrichmentListItem {
	[key: string]: unknown;
}

export async function getEnrichment(id: string): Promise<EnrichmentDetail> {
	const res = await authFetch(`${API_BASE_URL}/enrichment/${id}`);
	if (!res.ok) throw new Error("Failed to get enrichment");
	return res.json();
}

// --- Enrichment rows ---

export async function getEnrichmentRows(
	enrichmentId: string,
	page: number = 1,
	pageSize: number = 20
): Promise<FileRowsResponse> {
	const q = toQuery({
		page: String(page),
		pageSize: String(pageSize),
	});
	const res = await authFetch(
		`${API_BASE_URL}/enrichment/${enrichmentId}/rows${q}`
	);
	if (!res.ok) throw new Error("Failed to load enrichment rows");
	return res.json();
}

// --- Download result ---

export interface DownloadResponse {
	downloadUrl: string;
}

export async function getEnrichmentDownloadUrl(id: string): Promise<string> {
	const res = await authFetch(`${API_BASE_URL}/enrichment/${id}/download`);
	if (!res.ok) throw new Error("Failed to get download URL");
	const data: DownloadResponse = await res.json();
	return data.downloadUrl;
}
